import adskLogo from "@assets/img/adsk-logo.png";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import { Avatar, Badge, Divider, ListItemIcon, Menu, MenuItem, ThemeProvider, Typography, createTheme, styled } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
//import adskLogo from "@assets/img/img-png-wsp-red.png";
import { FeatureManagementType } from "@api/apiClient";
import { useDocumentNames, useDownloadDocument } from "@api/documents";
import Breadcrumbs from "@components/Breadcrumbs/Breadcrumbs";
import CircularProgressWithContent from "@components/CircularProgressWithContent/CircularProgressWithContent";
import { ErrorList } from "@components/Errors/ErrorList";
import JobProgressPanel from "@components/JobProgressPanel/JobProgressPanel";
import ThemableImage from "@components/ThemableImage";
import useAppStore from "@hooks/useAppStore";
import useUserPermissionStore from "@hooks/useUserPermissionStore";
import i18n, { supportedLanguages } from "@i18n/i18n";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import * as locales from "@mui/material/locale";
import { useLocation } from "react-router-dom";

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
}));

const Header = observer(({ message }: { message: string }) => {
  const appStore = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();
  const upStore = useUserPermissionStore();
  const [showBreadcrumb, setShowBreadcrumb] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [infoSubmenuAnchorEl, setInfoSubmenuAnchorEl] = useState(null);
  const [jobPanelOpen, setJobPanelOpen] = useState(false);
  const open = Boolean(anchorEl);
  const documentNames = useDocumentNames();
  const documents = documentNames?.data || [];
  const [filename, setFilename] = useState("");
  const mutateDownload = useDownloadDocument(filename);

  const darkTheme = useMemo(() => {
    const muiLocale = supportedLanguages.find((l) => l.locales.includes(appStore.language))?.mui || "enUS";
    // @ts-ignore No index signature with a parameter of type 'string' was found on type 'typeof
    const locale = locales[muiLocale];

    return createTheme(
      {
        palette: {
          mode: "dark",
          background: {
            default: appStore.colorMode === "light" ? "#121212" : "#000",
          },
        },
        typography: {
          button: {
            textTransform: "none",
          },
        },
        components: {
          MuiTypography: {
            styleOverrides: {
              root: {
                "&.MuiTypography-root": {
                  fontFamily: "ArtifaktElement, sans-serif",
                },
              },
            },
          },
        },
      },
      locale,
    );
  }, [appStore.colorMode, appStore.language]);

  const handleProfileClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
    setInfoSubmenuAnchorEl(null);
  };

  const handleSubmenuOpen = (event: any) => {
    setInfoSubmenuAnchorEl(event.currentTarget);
  };

  const handleSubmenuClose = () => {
    setInfoSubmenuAnchorEl(null);
  };

  const handleFileSelect = async (name: any) => {
    setFilename(name);
    await mutateDownload.mutateAsync(name)?.then((res) => {
      handleDownloadDocument(res.fileName, res.data);
    });
  };

  const handleDownloadDocument = (fileName: any, fileData: any) => {
    const blob = new Blob([fileData], { type: "application/octet-stream" });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = createDownloadLink(downloadUrl, fileName);
    triggerDownload(link);
    handleProfileMenuClose();
    setFilename("");
  };

  const createDownloadLink = (url: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    return link;
  };

  const triggerDownload = (link: HTMLAnchorElement) => {
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleProgressClick = () => {
    setJobPanelOpen(!jobPanelOpen);
  };

  // Count active jobs across all projects
  // Note: No useMemo needed - MobX observer will handle reactivity automatically
  let activeJobCount = 0;
  Object.keys(appStore.jobStatus.projects).forEach((projectId) => {
    Object.keys(appStore.jobStatus.projects[projectId]).forEach((modelId) => {
      const entry = appStore.jobStatus.projects[projectId][modelId];
      const hasActiveJobStatus = entry?.jobStatus?.workItemStatus?.status === "inprogress" || entry?.jobStatus?.workItemStatus?.status === "pending";
      const hasActiveProgress = entry?.progress !== undefined && entry?.progress.progress !== undefined && entry?.progress.progress < 100;
      if (hasActiveJobStatus || hasActiveProgress) {
        activeJobCount++;
      }
    });
  });

  const handleLogout = () => {
    setAnchorEl(null);
    navigate("/logout");
  };

  const handleTemsOfUse = () => {
    setInfoSubmenuAnchorEl(null);
    setAnchorEl(null);
    window.open("https://www.autodesk.com/company/terms-of-use/terms-of-use-changes", "_blank");
  };

  const handleProjects = () => {
    navigate("/projects");
  };

  const handleUserSettings = () => {
    setAnchorEl(null);
    appStore.toggleOpenSettings();
  };

  const handleAppSettings = () => {
    navigate("/settings");
  };
  
  const handleHelp = () => {
    window.open("https://arup.sharepoint.com/sites/network-decarbonisation-and-sustainability/SitePages/AutoDesk-ICT-trainings.aspx", "_blank");
  };


  function handleNavigate() {
    navigate("/projects");
  }

  useEffect(() => {
    setShowBreadcrumb(location.pathname.startsWith("/projects/"));
  }, [location.pathname]);

  return (
    <ThemeProvider theme={darkTheme}>
      <AppBar position="fixed" color="primary">
        <StyledToolbar>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              width: "stretch",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }} onClick={handleNavigate}>
              <ThemableImage src={adskLogo} height="40px" />
              <Typography
                sx={{
                  typography: { lg: "h5", xs: "body1" },
                  marginLeft: { md: "20px" },
                  display: { xs: "none", md: "block" },
                }}
              >
                {message}
              </Typography>
            </Box>
            {showBreadcrumb && <Breadcrumbs />}
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ErrorList open={appStore.errorManager.openErrors} />
                <Typography variant="subtitle2">v{import.meta.env.PACKAGE_VERSION}</Typography>
                {false && (
                  <Tooltip title={i18n.t("header.title.settings")}>
                    <div>
                      <IconButton onClick={appStore.toggleOpenSettings}>
                        <SettingsIcon />
                      </IconButton>
                    </div>
                  </Tooltip>
                )}
                {appStore.progressIndicator.showProgressIndicator && (
                  <Box ml={1} sx={{ position: "relative" }}>
                    <Tooltip title={i18n.t("header.title.progress-indicator")}>
                      <IconButton onClick={handleProgressClick} size="small">
                        <Badge badgeContent={activeJobCount} color="secondary" overlap="circular">
                          <CircularProgressWithContent content={<DirectionsRunIcon />} />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                    <ThemeProvider theme={appStore.defaultTheme}>
                      <JobProgressPanel open={jobPanelOpen} onClose={() => setJobPanelOpen(false)} />
                    </ThemeProvider>
                  </Box>
                )}
                <Tooltip title={i18n.t("header.title.profile")}>
                  <div>
                    <IconButton
                      onClick={handleProfileClick}
                      size="small"
                      sx={{ ml: 1 }}
                      aria-controls={open ? "profile-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={open ? "true" : undefined}
                    >
                      <Avatar src={appStore.userProfile?.imageUrl} />
                    </IconButton>
                  </div>
                </Tooltip>
              </Box>
              <Menu
                anchorEl={anchorEl}
                id="profile-menu"
                open={open}
                onClose={handleProfileMenuClose}
                slotProps={{
                  paper: {
                    elevation: 1,
                    sx: {
                      border: 1,
                      borderColor: "divider",
                      overflow: "visible",
                      "& .MuiList-root": {
                        padding: 0,
                      },
                      "& .MuiAvatar-root": {
                        width: 32,
                        height: 32,
                        ml: -0.5,
                        mr: 1,
                      },
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem component="div" disabled sx={{ "&.MuiMenuItem-root": { opacity: 1 } }}>
                  <Box mr={1} sx={{ display: "inline-block" }}>
                    <Avatar src={appStore.userProfile?.imageUrl} sx={{ width: "80px !important", height: "80px !important" }} />
                  </Box>
                  <Box sx={{ display: "inline-block" }}>
                    <Typography variant="h5">
                      {appStore.userProfile?.firstName} {appStore.userProfile?.lastName}
                    </Typography>
                    <Typography variant="subtitle1">{appStore.userProfile?.emailId}</Typography>
                    {upStore.primaryRole && <Typography variant="subtitle2">{i18n.t(`role-names.${upStore.primaryRole}`)}</Typography>}
                  </Box>
                </MenuItem>
                <Divider sx={{ margin: "0 !important" }} />
                <MenuItem onClick={handleProjects}>
                  <ListItemIcon>
                    <DashboardIcon fontSize="small" />
                  </ListItemIcon>
                  {i18n.t("header.title.projects")}
                </MenuItem>
                <MenuItem onClick={handleUserSettings}>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  {i18n.t("header.title.user-settings")}
                </MenuItem>
                {upStore.canAccessAppSettings() && <Divider sx={{ margin: "0 !important" }} />}
                {upStore.canAccessAppSettings() && (
                  <MenuItem onClick={handleAppSettings}>
                    <ListItemIcon>
                      <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    {i18n.t("header.title.app-settings")}
                  </MenuItem>
                )}
                <MenuItem onClick={handleSubmenuOpen}>
                  <ListItemIcon>
                    <HelpOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  {i18n.t("header.title.information")}
                </MenuItem>
                {appStore.hasFeature(FeatureManagementType.ExternalHelpDocument) && (
                  <MenuItem onClick={handleHelp}>
                    <ListItemIcon>
                      <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    {i18n.t("header.title.help")}
                  </MenuItem>
                )}
                

                <Menu
                  anchorEl={infoSubmenuAnchorEl}
                  open={Boolean(infoSubmenuAnchorEl)}
                  onClose={handleSubmenuClose}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  slotProps={{
                    paper: {
                      elevation: 1,
                      sx: {
                        border: 1,
                        borderColor: "divider",
                        overflow: "visible",
                        "& .MuiList-root": {
                          padding: 0,
                        },
                      },
                    },
                  }}
                >
                  <MenuItem onClick={handleTemsOfUse}>{i18n.t("header.title.terms-of-use")}</MenuItem>
                  {documents.map((data, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        handleFileSelect(data.fileName);
                      }}
                    >
                      {data.displayName}
                    </MenuItem>
                  ))}
                </Menu>
                <Divider sx={{ margin: "0 !important" }} />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  {i18n.t("header.title.sign-out")}
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </StyledToolbar>
      </AppBar>
      <Toolbar /* This adds padding above the content so they are not covered up by the AppBar */ />
    </ThemeProvider>
  );
});

export default Header;
